import React, { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Typewriter } from "react-simple-typewriter";
import "./web-offerings.scss";
import { accountCategoryURL, registrationFormURL } from "../../components/helpers/constant-words";
import { Link } from "react-router-dom";
import useTheme from "../../components/useTheme/useTheme";
import AutoplayVideo from "../../components/AutoplayVideo/AutoplayVideo";
import { archinzaChatVideoNew, chatImg, archinzaAIWebVideo } from "../../images";

// Configure the countdown target date here
// Set to null or a past date to show 00:00:00:00
// Example: const COUNTDOWN_TARGET_DATE = new Date("2026-03-01T00:00:00");
const COUNTDOWN_TARGET_DATE = null;

const WebOffering = ({ openPopup }) => {
  const { theme } = useTheme();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    // Don't run the interval if no countdown date is set
    if (!COUNTDOWN_TARGET_DATE) {
      return;
    }

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = COUNTDOWN_TARGET_DATE - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (value) => String(value).padStart(2, "0");

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hi, I'm exploring Archinza. Can you help me get started?");
    window.open(`https://wa.me/919871185558?text=${message}`, "_blank");
  };

  return (
    <section className="web-offerings">
      <div className="web-offerings__container">
        {/* Header */}
        <div className="web-offerings__header">
          <h2 className="web-offerings__title">Two Ways of Using Archinza</h2>
          <p
            className={`web-offerings__subtitle  ${
              theme === "dark" ? "dark_mode_text_muted--theme-styles" : ""
            }`}
          >
            <b>Search, Reach, Ask, Connect</b>
            <br />
      On the Web or WhatsApp, Archinza helps you discover relevant products, materials, and services, ask design or technical questions, reach experts and connect instantly on demand.
          </p>
        </div>

        <div className="web-offerings__content">
          {/* Visual Section */}
          <div className="web-offerings__visual-section web-offerings__visual-section--video">
            <AutoplayVideo
              className="web-offerings__video"
              videoSource={archinzaAIWebVideo}
              fallbackImg={chatImg}
              width="100%"
              height="100%"
            />
          </div>

          {/* Content Section */}
          <div className="web-offerings__text-section">
            <h1 className="web-offerings__section-title">
              Archinza AI Web
            </h1>

            <p className="web-offerings__description">
              Browse curated businesses, explore products, materials, expertise,
              save inspirations, all through an AI-powered platform.
            </p>

            <div className="web-offerings__button-container">
              <div className="web-offerings__button-wrapper">
                {/* <Link to={accountCategoryURL}> */}
                <Link to={registrationFormURL}>
                  <button
                    className="web-offerings__button web-offerings__button--primary"
                    // onClick={openPopup}
                  >
                    Get Started on Archinza Web
                    <ArrowRight className="web-offerings__button-icon" />
                  </button>
                </Link>
                <span className="web-offerings__button-subtext">Free for a limited time only</span>
              </div>

              {/* <button className="web-offerings__button web-offerings__button--secondary">
                Know More
                <ArrowRight className="web-offerings__button-icon" />
              </button> */}
            </div>
          </div>
        </div>

        {/* WhatsApp Section */}
        <div className="web-offerings__content web-offerings__content--reverse">
          {/* Visual Section */}
          <div className="web-offerings__visual-section web-offerings__visual-section--video">
            <AutoplayVideo
              className="web-offerings__video"
              videoSource={archinzaChatVideoNew}
              fallbackImg={chatImg}
              width="100%"
              height="100%"
            />
          </div>

          {/* Content Section */}
          <div className="web-offerings__text-section">
            <h1 className="web-offerings__section-title">
              Your AI-led design assistant
            </h1>

            <p className="web-offerings__description">
              Chat on WhatsApp to ask design questions, search for products and materials, and get instant answers.
            </p>

            <div className="web-offerings__button-container">
              <button
                className="web-offerings__button web-offerings__button--whatsapp"
                onClick={handleWhatsAppClick}
              >
                Chat Now!
                <ArrowRight className="web-offerings__button-icon" />
              </button>
            </div>
          </div>
        </div>
      </div>

        {/* Ask & Search Coming Soon Section - COMMENTED OUT
        <div className="web-offerings__search-section">
          <div className="web-offerings__search-image">
            <img
              src="/images/Personal_Listing.png"
              alt="Ask & Search feature preview"
              className="web-offerings__laptop-image"
            />
          </div>

          <div className="web-offerings__search-content">
            <p className="web-offerings__search-label">
              ASK & SEARCH FEATURE COMING SOON
            </p>
            <div className="web-offerings__search-description">
              <div className="web-offerings__visually-led-row">
                <em>Archinza's VISUALLY LED</em>
                <div className="web-offerings__countdown">
                  <div className="web-offerings__countdown-item">
                    <span className="web-offerings__countdown-value">{formatTime(timeLeft.days)}</span>
                    <span className="web-offerings__countdown-label">Days</span>
                  </div>
                  <span className="web-offerings__countdown-separator">:</span>
                  <div className="web-offerings__countdown-item">
                    <span className="web-offerings__countdown-value">{formatTime(timeLeft.hours)}</span>
                    <span className="web-offerings__countdown-label">Hours</span>
                  </div>
                  <span className="web-offerings__countdown-separator">:</span>
                  <div className="web-offerings__countdown-item">
                    <span className="web-offerings__countdown-value">{formatTime(timeLeft.minutes)}</span>
                    <span className="web-offerings__countdown-label">Minutes</span>
                  </div>
                  <span className="web-offerings__countdown-separator">:</span>
                  <div className="web-offerings__countdown-item">
                    <span className="web-offerings__countdown-value">{formatTime(timeLeft.seconds)}</span>
                    <span className="web-offerings__countdown-label">Seconds</span>
                  </div>
                </div>
              </div>
              <strong>Ask & Search</strong>{" "}
              <span className="web-offerings__ticker">
                <Typewriter
                  words={[
                    "designed to help you",
                    "see while you ask, and",
                    "ask while you search",
                  ]}
                  loop={true}
                  typeSpeed={70}
                  deleteSpeed={50}
                  delaySpeed={1500}
                />
              </span>
            </div>
            <p className="web-offerings__search-tagline">
              designed by architects for the industry
            </p>
          </div>
        </div>
        */}
    </section>
  );
};

export default WebOffering;
